import React, { useMemo } from 'react';
import { Patient } from '../../../shared/types/src';
import { ChartPieIcon, UserGroupIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface CohortSummaryPanelProps {
  patients: Patient[];
}

const StatCard: React.FC<{ icon: React.ElementType; title: string; value: string | number; color: string }> = ({ icon: Icon, title, value, color }) => {
    return (
        <div className="bg-white p-4 rounded-lg shadow flex items-center">
            <div className={`p-3 rounded-full mr-4 ${color}`}>
                <Icon className="h-6 w-6 text-white" />
            </div>
            <div>
                <p className="text-sm text-gray-500">{title}</p>
                <p className="text-2xl font-semibold text-gray-900">{value}</p>
            </div>
        </div>
    );
};


const CohortSummaryPanel: React.FC<CohortSummaryPanelProps> = ({ patients }) => {
  const stats = useMemo(() => {
    if (!patients || patients.length === 0) {
      return {
        totalPatients: 0,
        flaggedPatients: 0,
        needsAttentionCount: 0
      };
    }

    const needsAttentionCount = patients.filter(p => p.needs_attention).length;
    
    // Note: statusTags and riskFlag are not available in the shared Patient type
    // Using needs_attention as the flag indicator instead

    return {
      totalPatients: patients.length,
      flaggedPatients: needsAttentionCount,
      needsAttentionCount
    };
  }, [patients]);

  return (
    <div className="bg-gray-50 p-6 rounded-lg shadow-inner">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Cohort Overview</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={UserGroupIcon} title="Total Patients" value={stats.totalPatients} color="bg-blue-500" />
        <StatCard icon={ExclamationTriangleIcon} title="Flagged for Risk" value={stats.flaggedPatients} color="bg-red-500" />

        {/* No status counts available in the current implementation */}
      </div>
       <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Coming Soon</h3>
        <ul className="list-disc list-inside text-gray-600">
            <li>Average radar score per axis</li>
            <li>Breakdown of LLM type usage</li>
            <li>Average symbolic tags per week</li>
        </ul>
      </div>
    </div>
  );
};

export default CohortSummaryPanel; 